import moment from 'moment';
import notify from '@/plugins/notify';
import badgeBronze from '@/assets/badge_bronze.png';
import badgeSilver from '@/assets/badge_silver.png';
import badgeGold from '@/assets/badge_gold.png';
import badgePlatinum from '@/assets/badge_platinum.png';
import badgeDiamond from '@/assets/badge_diamond.png';
import badgeMasters from '@/assets/badge_masters.png';
import badgeGrandmaster from '@/assets/badge_grandmaster.png';
import {
    IRoomListState,
    IRoomResponse,
    IExtendedGameSettings,
} from './RoomList-types';
import { IPasswordModal } from '../PasswordModal/PasswordModal-types';
import api from '../../helpers/api';
import Router from '../../router';
import { loginInitialState } from '../Login/Login-state';
import { IGameStatus } from '../Game/Game-types';

const roomListState: IRoomListState = {
    state: {
        rooms: [],
        passwordModal: null,
    },
    mutations: {
        setRooms(state, rooms: IRoomResponse[]) {
			const recentWaiting = rooms.filter(room =>
				room.state === IGameStatus.WAITING_FOR_PLAYERS &&
					moment(room.createdAt).isAfter(moment().subtract(15, 'minutes')),
			);

			const others = rooms.filter(room =>
				!recentWaiting.includes(room),
			);

            state.rooms = [...recentWaiting, ...others];
        },
        closePasswordModal(state) {
            state.passwordModal = null;
        },
        setPasswordModal(state, options: IPasswordModal) {
            state.passwordModal = options;
        },
    },
    actions: {
        async getRooms({ commit }, name: string = '') {
            const query = name ? `?roomName=${name}` : '';
            const response = await api.request(`/game/rooms${query}`);

			if (response.ok) {
                commit('setRooms', await response.json());
			} else {
				commit('setRooms', []);
			}
        },
        setRooms({ commit }, rooms: IRoomResponse[]) {
            commit('setRooms', rooms);
        },
        closePasswordModal({ commit }) {
            commit('closePasswordModal');
        },
        setPasswordModal({ commit }, options: IPasswordModal) {
            commit('setPasswordModal', options);
        },
        async joinRoom({ commit, dispatch }, options) {
            const { gameId, password } = options;

            let payload: any = {
                gameId,
            };

            if (password) {
                payload = { ...payload, password };
            }

            const response = await api.request('/game/join', 'POST', payload);

            if (response.ok) {
                commit('closePasswordModal');
                Router.push(`/game/${gameId}`);
            } else if (response.status === 409) {
                dispatch('openModal', { type: 'leaveAllGames', data: payload });
            } else {
                const error = await response.json();
                let message = error.message;

                if (response.status === 401) {
                    message = 'You must be logged in to join a game';
                    dispatch('setAuthDetails', loginInitialState);
                }

                notify({
                    group: 'app',
                    clean: true,
                });

				if (message.includes('only allows the following ranks')) {
						const parseRanksToHtml = (text: string) => {
							const rankImages: Record<string, string> = {
								bronze: badgeBronze,
								silver: badgeSilver,
								gold: badgeGold,
								platinum: badgePlatinum,
								diamond: badgeDiamond,
								masters: badgeMasters,
								grandmaster: badgeGrandmaster,
							};

							return text.replace(/\b(bronze|silver|gold|platinum|diamond|masters|grandmaster)\b/gi, (rank) => {
								const url = rankImages[rank.toLowerCase()];
								return `<img src="${url}" alt="${rank}" style="height:14px;margin-right:4px;" />`;
							}
						);
					};

					notify({
						group: 'rank',
						clean: true,
					});

					notify({
						group: 'rank',
						title: "You can't join this room",
						type: 'error',
						text: parseRanksToHtml(message),
						duration: 4000,
					});
				} else {
					notify({
						group: 'app',
						title: 'Error',
						type: 'error',
						text: message,
					});
				}
            }
        },
        async createGame({ dispatch }, settings: IExtendedGameSettings) {
            const resolvedSettings = {
                ...settings,
                numOfPlayers: settings.numOfPlayers === '2-full-board' ? '2' : settings.numOfPlayers,
            };

            const response = await api.request('/game/create', 'POST', resolvedSettings);
            let errorTitle = '';
            let errorType = 'info';
            let errorMessage = 'You must be logged in to create a new game';

            if (response.ok) {
                const game = await response.json();
                Router.push(`/game/${game.gameId}`);

                notify({
                    group: 'app',
                    title: 'Success!',
                    type: 'success',
                    text: 'A new game was created successfully',
                });
                return;
            } else if (response.status === 409) {
                dispatch('openModal', { type: 'leaveAllGames', data: resolvedSettings });
                return;
            } else if (response.status === 401) {
                dispatch('setAuthDetails', loginInitialState);
            } else {
                const error = await response.json();
                errorTitle = 'Error';
                errorType = 'error';
                errorMessage = error.message;
            }

            notify({
                group: 'app',
                clean: true,
            });

			notify({
				group: 'app',
				title: errorTitle,
				type: errorType,
				text: errorMessage,
			});
        },
    },
};

export default roomListState;
